import React, { useState, useMemo, useEffect } from "react";
import type { AxiosInstance } from "axios";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericTabs from "@ui-components/Tabs/GenericTabs";
import GenericButton from "@ui-components/Button/GenericButton";
import {
  SkeletonBlock,
  TableSkeleton,
} from "@ui-components/Skeleton/GenericSkeleton";
import { DataStateView } from "../StateViews";
import {
  routers,
  GLOBAL_RX_LIMIT,
  GLOBAL_TX_LIMIT,
} from "../../backbone.data";
import { Connector } from "../../backbone.types";
import { UsageBar } from "../BackboneCharts";
import { ReactComponent as IconEye } from "../../../../../shared/assets/Icons/Icon_Eye.svg";
import {
  buildPredicate,
  type FilterField,
  labelId,
  parseToken,
  type ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import {
  makeBgpSessionFields,
  BGP_SESSION_TABLE_HEADERS,
  BGP_MODAL_ROUTE_SEARCH_KEYS,
} from "./bgpSessionFieldSpec";
import {
  fetchBbConnectorBgp,
  fetchBbConnectorRoutes,
  type BbConnectorBgpResponseData,
  type BbConnectorRouteItem,
} from "../../requests/request";
import {
  bgpSessionRowToBgpStatusFilterId,
  connectorTypeToGroup,
  matchesBgpStatusFilterSelection,
  matchesConnectorTypeGroupSelection,
} from "../BackboneDetailsData";

function formatBgpUptime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const parts: string[] = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0 || d > 0) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return parts.join(" ");
}

interface BGPTabProps {
  vrfFilterSelection?: string[];
  connectorTypeFilterSelection?: string[];
  bgpStatusFilterSelection?: string[];
  /** BGP session rows (from GET …/bbaas/:id/bgp-sessions), mapped to {@link Connector} for this UI. */
  sessions: Connector[];
  /** When true, shows skeleton loaders for summary cards and BGP sessions table. */
  loading?: boolean;
  /** When set, shows an error state instead of BGP content. */
  error?: string | null;
  /** Called when the user clicks Retry in the error state. */
  onRetry?: () => void;
  /** Used to load per-connector BGP detail in the session modal (`fetchBbConnectorBgp`). */
  api: AxiosInstance;
  tenantId: number | null;
}

const BGPTab: React.FC<BGPTabProps> = ({
  vrfFilterSelection = [],
  connectorTypeFilterSelection = [],
  bgpStatusFilterSelection = [],
  sessions,
  loading = false,
  error = null,
  onRetry,
  api,
  tenantId,
}) => {
  const [selectedEp, setSelectedEp] = useState<Connector | null>(null);
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const [bgpModalTab, setBgpModalTab] = useState<"details" | "routes">("details");
  const [modalRouteFilters, setModalRouteFilters] = useState<ParsedFilter[]>([]);
  const [bbConnectorBgpDetail, setBbConnectorBgpDetail] =
    useState<BbConnectorBgpResponseData | null>(null);
  const [bbConnectorBgpLoading, setBbConnectorBgpLoading] = useState(false);
  const [bbConnectorBgpError, setBbConnectorBgpError] = useState<string | null>(
    null
  );
  const [bbConnectorRoutes, setBbConnectorRoutes] = useState<BbConnectorRouteItem[]>([]);
  const [bbConnectorRoutesLoading, setBbConnectorRoutesLoading] = useState(false);
  const [bbConnectorRoutesError, setBbConnectorRoutesError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedEp) {
      setBgpModalTab("details");
      setModalRouteFilters([]);
      setBbConnectorRoutes([]);
      setBbConnectorRoutesError(null);
    }
  }, [selectedEp?.id]);

  useEffect(() => {
    if (selectedEp == null) {
      setBbConnectorBgpDetail(null);
      setBbConnectorBgpError(null);
      setBbConnectorBgpLoading(false);
      return;
    }
    const connectorId = selectedEp.bbaasConnectorId;
    if (connectorId == null || tenantId == null) {
      setBbConnectorBgpDetail(null);
      setBbConnectorBgpError(null);
      setBbConnectorBgpLoading(false);
      return;
    }
    let cancelled = false;
    setBbConnectorBgpLoading(true);
    setBbConnectorBgpError(null);
    setBbConnectorBgpDetail(null);
    fetchBbConnectorBgp(api, tenantId, connectorId)
      .then((res) => {
        if (cancelled) return;
        const body = res.data;
        if (body?.success && body.data) {
          setBbConnectorBgpDetail(body.data);
          setBbConnectorBgpError(null);
        } else {
          setBbConnectorBgpDetail(null);
          setBbConnectorBgpError(body?.message ?? "Failed to load BGP session");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setBbConnectorBgpDetail(null);
        setBbConnectorBgpError(
          err instanceof Error ? err.message : "Failed to load BGP session"
        );
      })
      .finally(() => {
        if (!cancelled) setBbConnectorBgpLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedEp?.id, selectedEp?.bbaasConnectorId, api, tenantId]);

  useEffect(() => {
    if (bgpModalTab !== "routes" || selectedEp == null) return;
    const connectorId = selectedEp.bbaasConnectorId;
    if (connectorId == null || tenantId == null) {
      setBbConnectorRoutes([]);
      setBbConnectorRoutesError(null);
      setBbConnectorRoutesLoading(false);
      return;
    }
    if (bbConnectorRoutes.length > 0) return;
    let cancelled = false;
    setBbConnectorRoutesLoading(true);
    setBbConnectorRoutesError(null);
    fetchBbConnectorRoutes(api, tenantId, connectorId)
      .then((res) => {
        if (cancelled) return;
        const body = res.data;
        if (body?.success && body.data) {
          setBbConnectorRoutes(body.data.routes ?? []);
          setBbConnectorRoutesError(null);
        } else {
          setBbConnectorRoutes([]);
          setBbConnectorRoutesError(body?.message ?? "Failed to load routes");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setBbConnectorRoutes([]);
        setBbConnectorRoutesError(
          err instanceof Error ? err.message : "Failed to load routes"
        );
      })
      .finally(() => {
        if (!cancelled) setBbConnectorRoutesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bgpModalTab, selectedEp?.id, selectedEp?.bbaasConnectorId, api, tenantId]);

  const modalBgpEstablished = useMemo(
    () =>
      selectedEp != null
        ? bbConnectorBgpDetail?.bgpSession != null
          ? bbConnectorBgpDetail.bgpSession.established
          : selectedEp.bgp === "Established"
        : false,
    [selectedEp, bbConnectorBgpDetail]
  );

  const data = useMemo(() => {
    return sessions.filter((e) => {
      if (vrfFilterSelection.length > 0) {
        if (e.vrf == null || !vrfFilterSelection.includes(e.vrf)) return false;
      }
      if (connectorTypeFilterSelection.length > 0) {
        const group = connectorTypeToGroup(e.type);
        if (!matchesConnectorTypeGroupSelection(group, connectorTypeFilterSelection))
          return false;
      }
      if (bgpStatusFilterSelection.length > 0) {
        if (
          !matchesBgpStatusFilterSelection(
            bgpSessionRowToBgpStatusFilterId(e.bgp),
            bgpStatusFilterSelection
          )
        )
          return false;
      }
      return true;
    });
  }, [
    vrfFilterSelection,
    connectorTypeFilterSelection,
    bgpStatusFilterSelection,
    sessions,
  ]);

  const sessionFields = useMemo(() => makeBgpSessionFields(), []);
  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<Connector>>();
    for (const f of sessionFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [sessionFields]);
  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of sessionFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [sessionFields]);
  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);
      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }
      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else {
          token.op = ":";
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);
  const predicate = useMemo(
    () => buildPredicate<Connector>(normalizedFilters, sessionFields),
    [normalizedFilters, sessionFields]
  );
  const filteredData = useMemo(() => data.filter(predicate), [data, predicate]);

  const totalRx = filteredData.reduce((s, e) => s + e.rxP, 0);
  const totalTx = filteredData.reduce((s, e) => s + e.txP, 0);
  const sessionsUp = filteredData.filter((e) => e.bgp === "Established").length;

  const apiRouteColumns = useMemo<GenericTableColumn<BbConnectorRouteItem>[]>(
    () => [
      {
        header: "Prefix",
        wrapMode: "nowrap",
        renderCell: (r) => (
          <span className="font-mono text-xs font-medium leading-tight text-semantic-text-primary tabular-nums">
            {r.prefix}
          </span>
        ),
      },
      {
        header: "LP",
        wrapMode: "nowrap",
        renderCell: (r) => (
          <span
            className={`block text-left text-xs tabular-nums ${
              r.lp !== 100
                ? "font-medium text-[var(--color-semantic-text-warning)]"
                : "font-normal text-semantic-text-primary"
            }`}
          >
            {r.lp}
          </span>
        ),
      },
      {
        header: "MED",
        wrapMode: "nowrap",
        renderCell: (r) => (
          <span className="block text-left font-mono text-xs tabular-nums text-semantic-text-secondary">
            {r.med}
          </span>
        ),
      },
      {
        header: "AS Path",
        wrapMode: "truncate",
        renderCell: (r) => (
          <span
            className="block max-w-full truncate font-mono text-xs leading-tight text-semantic-text-secondary"
            title={r.asPath ?? ""}
          >
            {r.asPath ?? "—"}
          </span>
        ),
      },
      {
        header: "Origin",
        wrapMode: "nowrap",
        renderCell: (r) => (
          <span className="text-xs text-semantic-text-secondary">
            {r.origin ?? "—"}
          </span>
        ),
      },
      {
        header: "Best",
        wrapMode: "nowrap",
        renderCell: (r) => (
          <span
            className={
              r.best
                ? "font-semibold text-semantic-text-brand"
                : "text-semantic-text-secondary"
            }
          >
            {r.best ? "\u2713" : "\u2014"}
          </span>
        ),
      },
    ],
    []
  );

  const filteredApiRoutes = useMemo(() => {
    if (bbConnectorRoutes.length === 0) return [];
    if (modalRouteFilters.length === 0) return bbConnectorRoutes;
    const searchValue = modalRouteFilters
      .map((f) => f.value)
      .join(" ")
      .toLowerCase();
    if (!searchValue) return bbConnectorRoutes;
    return bbConnectorRoutes.filter(
      (r) =>
        r.prefix?.toLowerCase().includes(searchValue) ||
        r.asPath?.toLowerCase().includes(searchValue) ||
        r.origin?.toLowerCase().includes(searchValue) ||
        String(r.lp).includes(searchValue) ||
        String(r.med).includes(searchValue)
    );
  }, [bbConnectorRoutes, modalRouteFilters]);

  const sessionColumns: GenericTableColumn<Connector>[] = [
    {
      header: "Label",
      renderCell: (ep) => {
        const subscriptionLabel =
          ep.subscriptionId != null && String(ep.subscriptionId).trim() !== ""
            ? String(ep.subscriptionId).trim()
            : "-";
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-medium text-semantic-text-primary">
              {ep.label}
            </span>
            <span className="text-xs text-semantic-text-secondary">
              {subscriptionLabel} / ASN {ep.asn}
            </span>
          </div>
        );
      },
    },
    {
      header: "VRF",
      width: "100px",
      renderCell: (ep) => (
        <GenericTag label={ep.vrf} variant="neutral" size="small" />
      ),
    },
    {
      header: "Peer IP",
      width: "140px",
      renderCell: (ep) => (
        <span className="font-mono text-xs text-semantic-text-secondary tabular-nums">
          {ep.peerIp}
        </span>
      ),
    },
    {
      header: "Local IP",
      width: "140px",
      renderCell: (ep) => (
        <span className="font-mono text-xs text-semantic-text-secondary tabular-nums">
          {ep.ip != null && String(ep.ip).trim() !== "" && ep.ip !== "—"
            ? ep.ip
            : "—"}
        </span>
      ),
    },
    {
      header: "Local ASN",
      width: "100px",
      renderCell: (ep) => (
        <span className="font-mono text-xs text-semantic-text-secondary tabular-nums">
          {ep.localAsn != null && Number.isFinite(ep.localAsn)
            ? ep.localAsn
            : "—"}
        </span>
      ),
    },
    {
      header: "Status",
      width: "80px",
      renderCell: (ep) => (
        <GenericTag
          label={ep.bgp === "Established" ? "Up" : "Down"}
          variant={ep.bgp === "Established" ? "success" : "error"}
          size="small"
        />
      ),
    },
    {
      header: "Received",
      width: "100px",
      renderCell: (ep) => (
        <span className="text-xs text-semantic-text-secondary tabular-nums">
          <span className="font-medium text-semantic-text-primary">
            {ep.rxP}
          </span>
          {ep.rxLimit != null && <>/{ep.rxLimit}</>}
        </span>
      ),
    },
    {
      header: "Advertised",
      width: "100px",
      renderCell: (ep) => (
        <span className="text-xs text-semantic-text-secondary tabular-nums">
          <span className="font-medium text-semantic-text-primary">
            {ep.txP}
          </span>
          {ep.txLimit != null && <>/{ep.txLimit}</>}
        </span>
      ),
    },
    {
      header: "Detail",
      width: "80px",
      renderCell: (ep) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedEp(ep);
          }}
          className="p-1 rounded hover:bg-semantic-bg-tertiary text-semantic-text-secondary hover:text-semantic-text-primary transition-colors"
          aria-label="View details"
        >
          <IconEye className="w-5 h-5" />
        </button>
      ),
    },
  ];

  return (
    <>
      <DataStateView
        loading={loading}
        error={error}
        isEmpty={data.length === 0}
        onRetry={onRetry}
        loadingContent={
          <>
            <GenericCard
              isCollapsible={false}
              className="mt-4"
              headerContent={
                <h2 className="text-md font-bold text-semantic-text-primary">
                  Details
                </h2>
              }
              bodyContent={
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xs font-medium text-semantic-text-secondary mb-3">
                      BGP Summary
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <SkeletonBlock h={64} className="rounded-md" />
                      <SkeletonBlock h={64} className="rounded-md" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xs font-medium text-semantic-text-secondary mb-3">
                      Route limits &mdash; global
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <SkeletonBlock h={64} className="rounded-md" />
                      <SkeletonBlock h={64} className="rounded-md" />
                    </div>
                  </div>
                </div>
              }
            />
            <GenericCard
              isCollapsible={false}
              className="mt-4"
              headerContent={
                <h2 className="text-md font-bold text-semantic-text-primary">
                  BGP Sessions
                </h2>
              }
              bodyContent={
                <div className="flex flex-col min-h-0">
                  <div className="mb-4 w-full">
                    <SkeletonBlock h={40} w="100%" className="rounded" />
                  </div>
                  <TableSkeleton columns={8} rows={8} />
                </div>
              }
            />
          </>
        }
        errorTitle="Failed to load BGP sessions"
        emptyTitle={
          vrfFilterSelection.length === 0 &&
          connectorTypeFilterSelection.length === 0 &&
          bgpStatusFilterSelection.length === 0
            ? "You have no BGP sessions yet"
            : "No BGP sessions for current filters"
        }
        emptyDescription={
          vrfFilterSelection.length === 0 &&
          connectorTypeFilterSelection.length === 0 &&
          bgpStatusFilterSelection.length === 0
            ? "This backbone has no BGP sessions. Connectors with BGP will appear here when available."
            : "There are no BGP sessions matching the active filters. Try changing or clearing them."
        }
      >
        {/* Details: BGP Summary + Route limits - global in one container */}
        <GenericCard
          isCollapsible={false}
          className="mt-4"
          headerContent={
            <h2 className="text-md font-bold text-semantic-text-primary">
              Details
            </h2>
          }
          bodyContent={
            <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-3">
              {/* BGP Summary — 1/3 width; Sessions | Up in one card */}
              <div className="min-w-0 lg:col-span-1">
                <h3 className="mb-3 text-xs font-medium text-semantic-text-secondary">
                  BGP Summary
                </h3>
                <GenericCard
                  isCollapsible={false}
                  variant="primary"
                  className="min-w-0"
                  bodyContent={
                    <div className="grid min-w-0 grid-cols-2 gap-3 sm:gap-4">
                      <div className="flex min-w-0 justify-center border-r border-semantic-border-primary pr-3 sm:pr-4">
                        <div className="inline-block text-left">
                          <span className="text-xs text-semantic-text-secondary">
                            Sessions
                          </span>
                          <span className="mt-1 block text-sm font-medium text-semantic-text-brand tabular-nums">
                            {filteredData.length}
                          </span>
                        </div>
                      </div>
                      <div className="flex min-w-0 justify-center">
                        <div className="inline-block text-left">
                          <span className="text-xs text-semantic-text-secondary">
                            Up
                          </span>
                          <span className="mt-1 block text-sm font-medium text-semantic-text-brand tabular-nums">
                            {sessionsUp} of {filteredData.length}
                          </span>
                        </div>
                      </div>
                    </div>
                  }
                />
              </div>

              {/* Route limits — global: 2/3 width; inner cards 50% / 50% */}
              <div className="min-w-0 lg:col-span-2">
                <h3 className="mb-3 text-xs font-medium text-semantic-text-secondary">
                  Route limits &mdash; global
                </h3>
                <div className="grid min-w-0 grid-cols-2 gap-3 sm:gap-4">
                  <GenericCard
                    isCollapsible={false}
                    variant="primary"
                    className="min-w-0"
                    bodyContent={
                      <>
                        <span className="text-xs text-semantic-text-secondary">
                          Total prefixes received
                        </span>
                        <span className="mt-1 block text-sm font-medium text-semantic-text-brand">
                          {totalRx} / {GLOBAL_RX_LIMIT}
                        </span>
                        <div className="mt-2">
                          <UsageBar
                            used={totalRx}
                            cap={GLOBAL_RX_LIMIT}
                            height={6}
                            barClassName="bg-[var(--color-semantic-bg-brand-bold)]"
                          />
                        </div>
                      </>
                    }
                  />
                  <GenericCard
                    isCollapsible={false}
                    variant="primary"
                    className="min-w-0"
                    bodyContent={
                      <>
                        <span className="text-xs text-semantic-text-secondary">
                          Prefixes advertised
                        </span>
                        <span className="mt-1 block text-sm font-medium text-semantic-text-brand">
                          {totalTx} / {GLOBAL_TX_LIMIT}
                        </span>
                        <div className="mt-2">
                          <UsageBar
                            used={totalTx}
                            cap={GLOBAL_TX_LIMIT}
                            height={6}
                            barClassName="bg-[var(--color-semantic-bg-brand-bold)]"
                          />
                        </div>
                      </>
                    }
                  />
                </div>
              </div>
            </div>
          }
        />

        {/* BGP sessions table */}
        <GenericCard
          isCollapsible={false}
          className="mt-4"
          headerContent={
            <h2 className="text-md font-bold text-semantic-text-primary">
              BGP Sessions {loading || error ? "" : `(${filteredData.length})`}
              {!loading && !error && data.length !== filteredData.length && (
                <span className="text-semantic-text-secondary font-normal ml-1">
                  / {data.length}
                </span>
              )}
            </h2>
          }
          bodyContent={
            <div className="flex flex-col min-h-0">
              <div className="mb-4 flex-shrink-0 w-full min-w-0 sm:w-1/2 sm:max-w-[50%]">
                <GenericSearchbar
                  placeholder="Find by attribute or value"
                  suggestedKeys={BGP_SESSION_TABLE_HEADERS}
                  onFiltersChange={setActiveFilters}
                  inputMaxWidth="100%"
                />
              </div>
              <div className="min-w-0">
                <GenericTable<Connector>
                  columns={sessionColumns}
                  data={filteredData}
                  initialRowsPerPage={10}
                  rowsPerPageOptions={[10, 20, 30]}
                  width="100%"
                  tableClassName="bg-semantic-bg-primary"
                  maxTableBodyHeight="100%"
                  rowKey={(row) => String(row.id)}
                  isFooterVisible={true}
                  horizontalScroll="always"
                />
              </div>
            </div>
          }
        />
      </DataStateView>

      {/* BGP session detail modal */}
      <GenericModal
        isOpen={selectedEp !== null}
        onClose={() => setSelectedEp(null)}
        title={
          selectedEp ? (
            <span className="flex w-full items-center gap-2 pr-2">
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-semantic-bg-secondary"
                aria-hidden
              >
                <IconEye className="h-4 w-4 text-semantic-text-secondary" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-base font-bold leading-tight text-semantic-text-primary">
                  BGP session
                </span>
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs text-semantic-text-secondary">
                    {bbConnectorBgpDetail?.connectorName ?? selectedEp.label}
                  </span>
                  <GenericTag
                    label={modalBgpEstablished ? "Up" : "Down"}
                    variant={modalBgpEstablished ? "success" : "error"}
                    size="small"
                    rounded="full"
                    className="!px-1.5 !py-0 text-[10px] font-medium leading-none"
                  />
                </span>
              </span>
            </span>
          ) : undefined
        }
        size="lg"
        customWidth="max-w-3xl"
        maxHeight="85vh"
        footer={
          <GenericButton
            label="Close"
            variant="secondary"
            size="sm"
            onClick={() => setSelectedEp(null)}
          />
        }
      >
        {selectedEp &&
          (() => {
            const ep = selectedEp;
            const s = bbConnectorBgpDetail?.bgpSession;
            const rxP = s?.rxPrefixes ?? ep.rxP;
            const txP = s?.txPrefixes ?? ep.txP;
            const router = routers.find((r) => r.id === ep.router);
            const remainingRx =
              ep.rxLimit != null ? Math.max(0, ep.rxLimit - rxP) : null;
            const remainingTx =
              ep.txLimit != null ? Math.max(0, ep.txLimit - txP) : null;
            const detailBgpUp =
              s != null ? s.established : ep.bgp === "Established";
            const routerDisplay =
              s?.router != null && String(s.router).trim() !== ""
                ? s.router
                : router?.name ?? "—";
            const uptimeDisplay =
              s != null ? formatBgpUptime(s.uptime) : ep.uptime;
            const localIpDisplay = s?.localIp ?? ep.ip;
            const peerIpDisplay = s?.peerIp ?? ep.peerIp;
            const asnDisplay = s?.remoteAsn ?? ep.asn;

            const showDetailSkeleton =
              bbConnectorBgpLoading && ep.bbaasConnectorId != null;

            const sessionDetailCells: {
              label: string;
              value: React.ReactNode;
            }[] = [
              { label: "VRF", value: ep.vrf },
              { label: "Local IP", value: localIpDisplay },
              { label: "Peer IP", value: peerIpDisplay },
              { label: "ASN", value: asnDisplay },
              {
                label: "Uptime",
                value: (
                  <span className="inline-flex items-center justify-end gap-1.5">
                    <span
                      className={`h-2 w-2 shrink-0 rounded-full ${
                        detailBgpUp
                          ? "bg-[var(--color-semantic-bg-success-bold)]"
                          : "bg-[var(--color-semantic-bg-error-bold)]"
                      }`}
                      aria-hidden
                    />
                    {uptimeDisplay}
                  </span>
                ),
              },
              { label: "Router", value: routerDisplay },
            ];

            return (
              <div className="-mx-6 -mt-4 px-4 pb-1">
                <GenericTabs
                  tabs={[
                    { label: "Details", value: "details" },
                    { label: "Routes", value: "routes" },
                  ]}
                  activeTab={bgpModalTab}
                  onTabChange={(v) =>
                    setBgpModalTab(v as "details" | "routes")
                  }
                  size="xs"
                  fullWidth
                  className="mb-3"
                />

                {bgpModalTab === "details" && (
                  <div className="space-y-3">
                    {bbConnectorBgpError != null && (
                      <p
                        className="text-xs text-semantic-text-error"
                        role="alert"
                      >
                        {bbConnectorBgpError}
                      </p>
                    )}
                    {showDetailSkeleton ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          <SkeletonBlock h={88} className="rounded-md" />
                          <SkeletonBlock h={88} className="rounded-md" />
                        </div>
                        <SkeletonBlock h={200} className="rounded-md w-full" />
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          <GenericCard
                            isCollapsible={false}
                            variant="primary"
                            shadow="flat"
                            className="[&>div:last-child>div]:!p-3"
                            bodyContent={
                              <>
                                <span className="text-[11px] leading-tight text-semantic-text-secondary">
                                  Total prefixes received
                                </span>
                                <div className="mt-0.5 text-lg font-bold leading-tight text-semantic-text-brand">
                                  <span>{rxP}</span>
                                  {ep.rxLimit != null && (
                                    <span className="text-sm font-semibold text-semantic-text-secondary">
                                      {" "}
                                      / {ep.rxLimit}
                                    </span>
                                  )}
                                </div>
                                {ep.rxLimit != null && (
                                  <>
                                    <div className="mt-1.5">
                                      <UsageBar
                                        used={rxP}
                                        cap={ep.rxLimit}
                                        height={4}
                                        barClassName="bg-[var(--color-semantic-bg-brand-bold)]"
                                      />
                                    </div>
                                    <p className="mt-1 text-[11px] leading-tight text-semantic-text-secondary">
                                      {remainingRx} remaining of limit
                                    </p>
                                  </>
                                )}
                              </>
                            }
                          />
                          <GenericCard
                            isCollapsible={false}
                            variant="primary"
                            shadow="flat"
                            className="[&>div:last-child>div]:!p-3"
                            bodyContent={
                              <>
                                <span className="text-[11px] leading-tight text-semantic-text-secondary">
                                  Prefixes advertised (Tx)
                                </span>
                                <div className="mt-0.5 text-lg font-bold leading-tight text-semantic-text-brand">
                                  <span>{txP}</span>
                                  {ep.txLimit != null && (
                                    <span className="text-sm font-semibold text-semantic-text-secondary">
                                      {" "}
                                      / {ep.txLimit}
                                    </span>
                                  )}
                                </div>
                                {ep.txLimit != null && (
                                  <>
                                    <div className="mt-1.5">
                                      <UsageBar
                                        used={txP}
                                        cap={ep.txLimit}
                                        height={4}
                                        barClassName="bg-[var(--color-semantic-bg-brand-bold)]"
                                      />
                                    </div>
                                    <p className="mt-1 text-[11px] leading-tight text-semantic-text-secondary">
                                      {remainingTx} remaining of limit
                                    </p>
                                  </>
                                )}
                              </>
                            }
                          />
                        </div>

                        <div className="rounded-md border border-semantic-border-primary bg-semantic-bg-primary text-xs">
                          {sessionDetailCells.map((cell, idx) => (
                            <div
                              key={cell.label}
                              className={`flex flex-row items-center justify-between gap-3 px-3 py-2 ${
                                idx < sessionDetailCells.length - 1
                                  ? "border-b border-semantic-border-primary"
                                  : ""
                              }`}
                            >
                              <span className="text-xs font-semibold text-semantic-text-primary shrink-0">
                                {cell.label}
                              </span>
                              <span className="text-xs font-normal text-semantic-text-secondary text-right min-w-0 break-all">
                                {cell.value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {bgpModalTab === "routes" && (
                  <div>
                    <div className="mb-2 flex flex-row flex-wrap items-center justify-between gap-2">
                      <span className="text-[11px] text-semantic-text-secondary shrink-0">
                        Items: {filteredApiRoutes.length}
                      </span>
                      <div className="w-full min-w-0 sm:ml-auto sm:w-1/2 sm:max-w-[50%] sm:shrink">
                        <GenericSearchbar
                          placeholder="Find by attribute or value"
                          suggestedKeys={BGP_MODAL_ROUTE_SEARCH_KEYS}
                          onFiltersChange={setModalRouteFilters}
                          inputMaxWidth="100%"
                          size="sm"
                        />
                      </div>
                    </div>
                    {bbConnectorRoutesError != null && (
                      <p className="text-xs text-semantic-text-danger mb-2" role="alert">
                        {bbConnectorRoutesError}
                      </p>
                    )}
                    {bbConnectorRoutesLoading ? (
                      <TableSkeleton columns={6} rows={5} />
                    ) : bbConnectorRoutes.length === 0 ? (
                      <p className="text-[11px] text-semantic-text-secondary">
                        No routes
                      </p>
                    ) : filteredApiRoutes.length === 0 ? (
                      <p className="text-[11px] text-semantic-text-secondary">
                        No routes match your filters.
                      </p>
                    ) : (
                      <GenericTable<BbConnectorRouteItem>
                        columns={apiRouteColumns}
                        data={filteredApiRoutes}
                        width="100%"
                        tableClassName="bg-semantic-bg-primary [&_thead_th]:py-1.5 [&_thead_th]:text-[11px] [&_thead_th]:font-semibold [&_tbody_td]:py-1 [&_tbody_td]:text-[11px]"
                        rowKey={(r, i) => `${r.prefix}-${r.lp}-${r.med}-${i}`}
                        isFooterVisible={true}
                        initialRowsPerPage={10}
                        rowsPerPageOptions={[10, 20, 30]}
                        maxTableBodyHeight="240px"
                        horizontalScroll="lg-fit"
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })()}
      </GenericModal>
    </>
  );
};

export default BGPTab;
